import React from 'react';
import { remoteDB, salesDB } from '../integrations/pouch/DBConn';
import { hasAssignedLocations } from '../utils/funcs';
import { axiosInstanceShoppaBackend } from '../integrations/axios.config';
import { getPendingAndProcessingSales } from '../integrations/react_query/queryFn';
import { useOnlineStatus } from './useOnline';
import { getSessionInfo } from '../utils/getSessionInfo';
import { useAuthenticate } from './useAuthenticate';
import { handleCouchError } from '../utils/handleCouchError';
import { captureException } from '../integrations/sentry/capture';

export const supportWebWorker = () => {
	return !!window.Worker;
};

export const useReplicateAndProcessSales = () => {
	const { userInfo } = getSessionInfo();

	const retrySync = async (salesData: { [key: string]: any }) => {
		try {
			await axiosInstanceShoppaBackend.post('/product/process-sales', { sale: salesData }, { withCredentials: true });
		} catch (error) {
			captureException(error, { fn: 'useReplicateAndProcessSales.retrySync' });
		}
	};

	const replicateSalesRef = React.useRef<PouchDB.Replication.Replication<{}> | null>(null);
	const liveReplicationRef = React.useRef<PouchDB.Replication.Replication<{}> | null>(null);

	const isOnline = useOnlineStatus();

	const processSalesWhenOnline = async () => {
		try {
			if (!isOnline) return;

			const salesDataResponse = await getPendingAndProcessingSales();

			if (salesDataResponse?.length <= 0) return;

			await Promise.all(salesDataResponse.map((sale: any) => retrySync(sale)));
		} catch (err) {
			captureException(err, { fn: 'useReplicateAndProcessSales.processSalesWhenOnline' });
		}
	};

	const onlineReplicationRef = React.useRef<PouchDB.Replication.Replication<{}> | null>(null);

	const replicateAndProcessWhenOnline = () => {
		onlineReplicationRef.current = salesDB()
			.replicate.to(remoteDB(userInfo?.merchantName), { retry: true })
			.on('complete', async () => {
				await processSalesWhenOnline();
			});
	};

	const isAuthenticated = useAuthenticate();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useEffect(() => {
		if (!isAuthenticated) {
			return;
		}

		if (!hasAssignedLocations(userInfo)) {
			return;
		}

		replicateSalesRef.current = salesDB()
			.replicate.to(remoteDB(userInfo?.merchantName), { retry: true })
			.on('complete', async (info) => {
				if (info.status === 'complete') {
					liveReplicationRef.current = salesDB()
						.replicate.to(remoteDB(userInfo?.merchantName), { live: true, retry: true })
						.on('error', (info) => {});
					//manual process sales on mount
					if (!supportWebWorker()) {
						if (!isOnline) return;

						processSalesWhenOnline();
					}
				}
			})
			.on('error', (err: any) => {
				handleCouchError(err);
			})
			.on('change', (info) => {});

		//if browser does not supoort web worker
		if (!supportWebWorker()) {
			window.addEventListener('online', replicateAndProcessWhenOnline);
		}

		return () => {
			if (!supportWebWorker()) {
				window.removeEventListener('online', replicateAndProcessWhenOnline);
			}

			if (replicateSalesRef.current) {
				replicateSalesRef.current.cancel();
			}

			if (liveReplicationRef.current) {
				liveReplicationRef.current.cancel();
			}

			if (onlineReplicationRef.current) {
				onlineReplicationRef.current.cancel();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
